/**
 * 旅遊轉團（後台管理員發起，前台報名頁承接）的資料解析與判定。
 *
 * issue PM_template#3013 / #3011：
 * 後台發起轉團時會做兩件事——把原訂單資料寫入 localStorage 的 `transferredData`，並開啟前台報名頁，
 * 網址帶有一次性授權憑證 `transferToken`。前台必須「以網址有沒有 transferToken」判斷這次是不是轉團，
 * 不能因為 localStorage 剛好有殘留資料就當成轉團，否則殘留資料會被帶進不相關的報名。
 *
 * 這個檔案只放不依賴 React／Redux 的純函式，預填、參加者 UI、送出前驗證都使用同一份判定結果。
 * 前台檢查只是防誤用，授權防線仍是後端的 transfer token 驗證。
 */

import { OriginalOrder } from 'api/travelOrder';

import storage from './storage';

/** 後台寫入 localStorage 的 key（無前綴，前後台共用，請維持與後台一致） */
export const TRANSFERRED_DATA_STORAGE_KEY = 'transferredData';

export interface TransferredParticipant {
	id: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export interface TransferredOrder {
	selector: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

/** 通過格式檢查的 transferredData（尚未比對梯次與期限） */
export interface ParsedTransferredData {
	order: TransferredOrder;
	participants: TransferredParticipant[];
	/** 後台管理員選定的目標梯次 */
	travelItineraryId: number;
	/** token 到期時間（毫秒時間戳） */
	expiresAt: number;
	/** 原始資料，供預填表單使用（維持後台給的格式） */
	raw: {
		order: TransferredOrder;
		participants: TransferredParticipant[];
	};
}

export type TransferInvalidReason =
	| 'DATA_MISSING' // 網址有 token，但 localStorage 沒有資料（例如離開過報名頁後由瀏覽器歷史返回）
	| 'DATA_INVALID' // JSON 壞掉、缺必要欄位，或舊版後台產生的資料（沒有 travelItineraryId／expiresAt）
	| 'ITINERARY_MISMATCH' // 資料綁定的目標梯次與目前網址的梯次不同
	| 'EXPIRED'; // 超過 expiresAt

export type TransferContext =
	| {
			/** 網址沒有 transferToken：一般報名 */
			status: 'normal';
	  }
	| {
			/** 網址有 transferToken，且轉團資料有效 */
			status: 'transfer';
			token: string;
			data: ParsedTransferredData;
	  }
	| {
			/** 網址有 transferToken，但轉團資料無法使用：不得預填，也不得送出（不降級成一般訂單） */
			status: 'invalid';
			reason: TransferInvalidReason;
	  };

/** 網址 query 的值可能是 string／string[]／undefined，統一成字串（取第一個，沒有則空字串） */
export const normalizeQueryValue = (value: unknown): string => {
	const first = Array.isArray(value) ? value[0] : value;
	return typeof first === 'string' ? first.trim() : '';
};

const toFiniteInteger = (value: unknown): number | null => {
	if (typeof value === 'number') {
		return Number.isInteger(value) ? value : null;
	}
	if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
		return parseInt(value.trim(), 10);
	}
	return null;
};

/**
 * 解析 localStorage 內的 transferredData。任何格式問題都回傳 null，不 throw。
 */
export const parseTransferredData = (raw: string | null): ParsedTransferredData | null => {
	if (!raw) {
		return null;
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch (e) {
		return null;
	}

	if (!parsed || typeof parsed !== 'object') {
		return null;
	}

	const { order, participants, travelItineraryId, expiresAt } = parsed as {
		order?: TransferredOrder;
		participants?: TransferredParticipant[];
		travelItineraryId?: unknown;
		expiresAt?: unknown;
	};

	if (
		!order ||
		typeof order !== 'object' ||
		typeof order.selector !== 'string' ||
		!order.selector
	) {
		return null;
	}

	if (!Array.isArray(participants) || participants.length === 0) {
		return null;
	}

	const participantsAreValid = participants.every(
		participant =>
			participant && typeof participant === 'object' && toFiniteInteger(participant.id) !== null,
	);
	if (!participantsAreValid) {
		return null;
	}

	const itineraryId = toFiniteInteger(travelItineraryId);
	if (itineraryId === null) {
		return null;
	}

	if (typeof expiresAt !== 'string') {
		return null;
	}
	const expiresAtMs = Date.parse(expiresAt);
	if (Number.isNaN(expiresAtMs)) {
		return null;
	}

	return {
		order,
		participants,
		travelItineraryId: itineraryId,
		expiresAt: expiresAtMs,
		raw: { order, participants },
	};
};

interface ResolveTransferContextParams {
	/** 網址上的 transferToken（未處理的 query 值） */
	token: unknown;
	/** 網址上的 travelItineraryId（目前要報名的梯次） */
	travelItineraryId: unknown;
	/** localStorage 內 transferredData 的原始字串 */
	rawTransferredData: string | null;
	/** 現在時間（毫秒時間戳），由呼叫端傳入以利測試 */
	now: number;
}

/**
 * 判定這次報名是一般報名、有效轉團，或無效的轉團入口。
 *
 * 判定順序：
 * 1. 網址沒有 transferToken → 一般報名（不管 localStorage 有沒有殘留資料）
 * 2. 有 token，但資料不存在／格式不合 → invalid
 * 3. 資料綁定的目標梯次 ≠ 網址梯次 → invalid（注意：比對的是「目標梯次」，不是原訂單的梯次；
 *    轉團本來就是換到不同梯次，拿原梯次比對會擋掉所有合法轉團）
 * 4. 已過 expiresAt → invalid
 */
export const resolveTransferContext = ({
	token,
	travelItineraryId,
	rawTransferredData,
	now,
}: ResolveTransferContextParams): TransferContext => {
	const transferToken = normalizeQueryValue(token);

	if (!transferToken) {
		return { status: 'normal' };
	}

	if (!rawTransferredData) {
		return { status: 'invalid', reason: 'DATA_MISSING' };
	}

	const data = parseTransferredData(rawTransferredData);
	if (!data) {
		return { status: 'invalid', reason: 'DATA_INVALID' };
	}

	const currentItineraryId = toFiniteInteger(normalizeQueryValue(travelItineraryId));
	if (currentItineraryId === null || currentItineraryId !== data.travelItineraryId) {
		return { status: 'invalid', reason: 'ITINERARY_MISMATCH' };
	}

	if (data.expiresAt <= now) {
		return { status: 'invalid', reason: 'EXPIRED' };
	}

	return { status: 'transfer', token: transferToken, data };
};

/**
 * 後端 transfer token 驗證失敗的原因代碼（422 的 data.reason、400 的 data 字串）。
 * 參考 speckit 003-transfer-token-hardening/contracts/transfer-token-api.md。
 */
export const TRANSFER_TOKEN_REASONS = [
	'TOKEN_MISSING',
	'TOKEN_INVALID',
	'TOKEN_REVOKED',
	'TOKEN_USED',
	'TOKEN_EXPIRED',
	'ORDER_MISMATCH',
	'PARTICIPANT_MISMATCH',
	'ITINERARY_MISMATCH',
];

/** 從後端回應的 data 取出轉團驗證失敗原因（422 為 { reason }，400 為字串）；不是轉團原因則回傳 null */
export const extractTransferFailureReason = (data: unknown): string | null => {
	const candidate =
		data && typeof data === 'object' && !Array.isArray(data)
			? (data as { reason?: unknown }).reason
			: data;

	return typeof candidate === 'string' && TRANSFER_TOKEN_REASONS.includes(candidate)
		? candidate
		: null;
};

export interface TransferQuery {
	transferToken?: unknown;
	travelItineraryId?: unknown;
}

/** 清除後台寫入的轉團資料（沒有前綴，與後台寫入時的 key 一致） */
export const clearTransferredData = () => {
	storage.removeItem(TRANSFERRED_DATA_STORAGE_KEY, false);
};

/** 依網址參數與目前 localStorage 內容判定轉團狀態（唯讀，不清除任何資料） */
export const readTransferContext = ({
	transferToken,
	travelItineraryId,
}: TransferQuery): TransferContext =>
	resolveTransferContext({
		token: transferToken,
		travelItineraryId,
		rawTransferredData: storage.getItem(TRANSFERRED_DATA_STORAGE_KEY, false),
		now: Date.now(),
	});

/**
 * 判定轉團狀態，並清除已經不可能再使用的殘留資料：
 * - 一般報名（網址沒有 token）：殘留資料一律清除，避免之後被誤用
 * - 轉團資料無效（格式不合、梯次不符、已過期）：清除
 * - 本來就沒有資料（DATA_MISSING）、或資料有效：不動
 */
export const settleTransferContext = (query: TransferQuery): TransferContext => {
	const context = readTransferContext(query);

	if (
		context.status === 'normal' ||
		(context.status === 'invalid' && context.reason !== 'DATA_MISSING')
	) {
		clearTransferredData();
	}

	return context;
};

/**
 * 組出送給後端的 originalOrder。
 * 一般報名固定回傳空物件（維持與後端 API 相容）；只有有效的轉團才帶原訂單 selector、原參加者與 token。
 * 無效的轉團不應該走到這裡（送出前已被擋下）。
 */
export const buildOriginalOrder = (context: TransferContext): OriginalOrder | {} => {
	if (context.status !== 'transfer') {
		return {};
	}

	return {
		orderSelector: context.data.order.selector,
		participants: context.data.participants.map(participant => Number(participant.id)),
		transferToken: context.token,
	};
};
